import { Field, FieldArray, useFormikContext } from 'formik';
import { Mail, Plus, Trash2 } from 'lucide-react';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field as UIField } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useInviteUserMutation } from '@/query/user.query';

import type { InviteUserFormValues } from '@/schemas/user.schema';

import { InviteUserFormProvider } from '@/formik/user.formik';
import type { Role } from '@/types/role.type';

type Props = {
  toggle: () => void;
  roles: Role[];
};

const InviteUserList = ({ roles }: { roles: Role[] }) => {
  const { values, setFieldTouched } = useFormikContext<InviteUserFormValues>();

  return (
    <>
      <FieldArray
        name="users"
        render={arrayHelpers => {
          return (
            <>
              {values.users.map((invite, index) => (
                <tr className="border-b hover:bg-muted/30" key={`invite-${index}`}>
                  <td className="p-3 align-top">
                    <div className="space-y-1">
                      <Field name={`users[${index}].email`}>
                        {({ field, meta }) => (
                          <UIField>
                            <Input
                              {...field}
                              id={`invite-email-${index}`}
                              type="email"
                              placeholder="member@example.com"
                              className="w-full"
                              aria-invalid={!!(meta.touched && meta.error)}
                            />
                          </UIField>
                        )}
                      </Field>
                      <ErrorField name={`users[${index}].email`} />
                    </div>
                  </td>
                  <td className="p-3 align-top">
                    <div className="space-y-1">
                      <Select
                        value={invite.role?.toString()}
                        onValueChange={value => {
                          arrayHelpers.replace(index, { ...invite, role: parseInt(value, 10) });
                          setFieldTouched(`users[${index}].role`, true, false);
                        }}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>

                        <SelectContent>
                          {roles.map(role => (
                            <SelectItem key={role.id} value={role.id.toString()}>
                              {role.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <ErrorField name={`users[${index}].role`} />
                    </div>
                  </td>
                  {values.users.length > 1 && (
                    <td className="p-3 align-top">
                      <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        onClick={() => arrayHelpers.remove(index)}
                        className="hover:bg-destructive/10 hover:text-destructive h-8 w-8"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </>
          );
        }}
      />
    </>
  );
};

const AddAnotherUser = () => {
  const { isValid, values, setFieldValue } = useFormikContext<InviteUserFormValues>();

  return (
    <Button
      variant="ghost"
      size={'sm'}
      type="button"
      disabled={!isValid}
      onClick={() => setFieldValue('users', [...values.users, { email: '', role: undefined }])}
      className="w-fit"
    >
      <Plus className="h-4 w-4" />
      Add another member
    </Button>
  );
};

const SendInvitationsButton = ({ isLoading }: { isLoading: boolean }) => {
  const { isValid } = useFormikContext<InviteUserFormValues>();

  return (
    <Button type="submit" disabled={!isValid || isLoading}>
      <Mail className="h-4 w-4" />
      Send invitations
    </Button>
  );
};

export const InviteUser = ({ toggle, roles }: Props) => {
  const mutation = useInviteUserMutation({
    onSuccess: () => {
      toggle();
    },
  });
  return (
    <Dialog open={true} onOpenChange={toggle}>
      <DialogContent className="max-w-2xl">
        <InviteUserFormProvider onSubmit={mutation.mutate}>
          <DialogHeader>
            <DialogTitle>Invite team members</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Invite multiple team members at once. Each member will receive an email invitation.
            </div>

            <div className="max-h-96 overflow-y-auto border rounded-lg">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left font-medium p-3 text-sm w-full">
                      <RequiredLabel>Email address</RequiredLabel>
                    </th>
                    <th className="text-left font-medium p-3 text-sm">
                      <RequiredLabel>Role</RequiredLabel>
                    </th>
                    <th className=""></th>
                  </tr>
                </thead>
                <tbody>
                  <InviteUserList roles={roles} />
                </tbody>
              </table>
            </div>

            <AddAnotherUser />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={toggle} type="button">
              Cancel
            </Button>
            <SendInvitationsButton isLoading={mutation.isPending} />
          </DialogFooter>
        </InviteUserFormProvider>
      </DialogContent>
    </Dialog>
  );
};
